/**
 * React Hook for Ruby AI MCP Context
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  mcpService,
  type AuthorityOverview,
  type PriorityQueueResponse,
  type PrioritizedIssue,
  type MarkFixedResponse,
} from "../services/mcpService";
import { dispatchIssueFixed, RUBY_ISSUE_FIXED_EVENT } from "../quick-fixes/cmsGuides";

export type { PrioritizedIssue, AuthorityOverview, MarkFixedResponse, PriorityQueueResponse };

interface UseMCPContextOptions {
  domain: string;
  reportId?: string | null;
  autoRefreshInterval?: number;
}

interface MCPContextState {
  overview: AuthorityOverview | null;
  priorityQueue: PriorityQueueResponse | null;
  allIssues: PrioritizedIssue[] | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

interface UseMCPContextReturn extends MCPContextState {
  totalIssues: number;
  pendingIssues: number;
  fixedIssues: number;
  highPriorityCount: number;
  recommendedCard: string | null;
  recommendedReason: string | null;
  topIssues: PrioritizedIssue[];

  refresh: () => Promise<void>;
  markIssueFixed: (issueId: string, cardId: string, note?: string) => Promise<MarkFixedResponse | null>;
  markIssueInProgress: (issueId: string) => Promise<boolean>;
  resetIssue: (issueId: string) => Promise<boolean>;

  getIssueById: (issueId: string) => PrioritizedIssue | undefined;
  getIssuesByCard: (cardId: string) => PrioritizedIssue[];
  generateFixPrompt: (issue: PrioritizedIssue, domain: string, contextBlock?: string) => Promise<string>;
  buildContextForClaude: () => Promise<string>;
}

export const useMCPContext = (options: UseMCPContextOptions): UseMCPContextReturn => {
  const { domain, reportId = null, autoRefreshInterval = 0 } = options;
  const fetchGenerationRef = useRef(0);

  const [state, setState] = useState<MCPContextState>({
    overview: null,
    priorityQueue: null,
    allIssues: null,
    isLoading: true,
    error: null,
    lastUpdated: null,
  });

  const fetchData = useCallback(async (force = false) => {
    const generation = ++fetchGenerationRef.current;
    if (!domain) {
      setState((prev) => ({
        ...prev,
        overview: null,
        priorityQueue: null,
        allIssues: null,
        isLoading: false,
        error: null,
      }));
      return;
    }

    setState({ overview: null, priorityQueue: null, allIssues: null,
      isLoading: true, error: null, lastUpdated: null });

    const res = await mcpService.getMcpContext(domain, {
      force,
      reportId,
      queueLimit: 50,
      issuesLimit: 100,
    });

    if (generation !== fetchGenerationRef.current) return;

    if (!res.success || !res.data) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: res.error?.message || 'Failed to load MCP data',
      }));
      return;
    }

    setState({
      overview: res.data.overview,
      priorityQueue: res.data.priorityQueue,
      allIssues: res.data.allIssues,
      isLoading: false,
      error: null,
      lastUpdated: new Date(),
    });
  }, [domain, reportId]);

  useEffect(() => {
    fetchData();
    return () => { ++fetchGenerationRef.current; };
  }, [fetchData]);

  useEffect(() => {
    if (autoRefreshInterval <= 0) return;
    const intervalId = setInterval(() => fetchData(true), autoRefreshInterval);
    return () => clearInterval(intervalId);
  }, [fetchData, autoRefreshInterval]);

  useEffect(() => {
    const onFixed = (e: Event) => {
      const detail = (e as CustomEvent<{ domain: string }>).detail;
      if (detail?.domain === domain) fetchData(true);
    };
    window.addEventListener(RUBY_ISSUE_FIXED_EVENT, onFixed);
    return () => window.removeEventListener(RUBY_ISSUE_FIXED_EVENT, onFixed);
  }, [domain, fetchData]);

  const scopedReportId = reportId || state.overview?.reportId || null;

  const markIssueFixed = useCallback(async (issueId: string, cardId: string, note?: string): Promise<MarkFixedResponse | null> => {
    const res = await mcpService.markIssueFixed(domain, issueId, cardId, note, scopedReportId);
    if (res.success) {
      dispatchIssueFixed({ issueId, domain });
      return res.data!;
    }
    return null;
  }, [domain, scopedReportId]);

  const markIssueInProgress = useCallback(async (issueId: string): Promise<boolean> => {
    const res = await mcpService.markIssueInProgress(domain, issueId, scopedReportId);
    if (res.success) {
      dispatchIssueFixed({ issueId, domain });
      return true;
    }
    return false;
  }, [domain, scopedReportId]);

  const resetIssue = useCallback(async (issueId: string): Promise<boolean> => {
    const res = await mcpService.resetIssueStatus(domain, issueId, scopedReportId);
    if (res.success) {
      dispatchIssueFixed({ issueId, domain });
      return true;
    }
    return false;
  }, [domain, scopedReportId]);

  const getIssueById = useCallback(
    (issueId: string): PrioritizedIssue | undefined =>
      state.allIssues?.find((i) => i.id === issueId) ??
      state.priorityQueue?.priorityQueue.find((i) => i.id === issueId),
    [state.allIssues, state.priorityQueue]
  );

  const getIssuesByCard = useCallback(
    (cardId: string): PrioritizedIssue[] => {
      const fromQueue = state.priorityQueue?.priorityQueue.filter((i) => i.card === cardId);
      if (fromQueue && fromQueue.length > 0) return fromQueue;
      return state.allIssues?.filter((i) => i.card === cardId) || [];
    },
    [state.allIssues, state.priorityQueue]
  );

  const generateFixPrompt = useCallback(
    async (issue: PrioritizedIssue, issueDomain: string, contextBlock?: string): Promise<string> =>
      mcpService.generateFixPrompt(issue, issueDomain, contextBlock),
    []
  );

  const buildContextForClaude = useCallback(async (): Promise<string> => {
    if (!domain) return "Unable to load authority context. Please try again.";
    return mcpService.buildRubyContext(domain, { reportId });
  }, [domain, reportId]);

  return {
    ...state,
    totalIssues: state.overview?.totalIssues.total || 0,
    pendingIssues: state.overview?.totalIssues.pending || 0,
    fixedIssues: state.overview?.totalIssues.fixed || 0,
    highPriorityCount: state.overview?.totalIssues.high || 0,
    recommendedCard: state.overview?.recommendedNextCard || null,
    recommendedReason: state.overview?.recommendedReason || null,
    topIssues: state.priorityQueue?.priorityQueue.slice(0, 10) || [],
    refresh: () => fetchData(true),
    markIssueFixed,
    markIssueInProgress,
    resetIssue,
    getIssueById,
    getIssuesByCard,
    generateFixPrompt,
    buildContextForClaude,
  };
};

